import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
    Menu, 
    X, 
    Upload, 
    LayoutDashboard, 
    TrendingUp, 
    MessageCircle, 
    User,
    LogOut,
    FileText,
    CreditCard,
    ChevronDown
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { user, logout, isAuthenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navLinks = [
        { href: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
        { href: '/upload', label: t('nav.upload'), icon: Upload },
        { href: '/trends', label: t('nav.trends'), icon: TrendingUp },
        { href: '/chat', label: t('nav.chat'), icon: MessageCircle },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="sticky top-0 z-50 glass border-b border-stone-200/50" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                        <img 
                            src="/logo.svg" 
                            alt={t('app.name')} 
                            className="h-10 w-auto group-hover:scale-105 transition-transform"
                        />
                        <div className="hidden sm:block">
                            <span className="text-lg font-bold text-stone-800">{t('app.name')}</span>
                            <span className="block text-xs text-stone-500 -mt-1">My Medical Results</span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    {isAuthenticated && (
                        <div className="hidden md:flex items-center gap-1">
                            {navLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        data-testid={`nav-${link.href.replace('/', '')}`}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                            isActive(link.href)
                                                ? 'bg-teal-500 text-white shadow-md shadow-teal-500/20'
                                                : 'text-stone-600 hover:text-teal-600 hover:bg-teal-50'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>
                    )}

                    {/* Right Side */}
                    <div className="flex items-center gap-3">
                        {/* Language Selector - Always visible */}
                        <LanguageSelector variant="minimal" />
                        
                        {isAuthenticated ? (
                            <>
                                {/* User Menu */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-stone-100"
                                            data-testid="user-menu-trigger"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-medium text-sm">
                                                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <span className="hidden sm:block text-sm font-medium text-stone-700">{user?.name}</span>
                                            <ChevronDown className="w-4 h-4 text-stone-500" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <div className="px-3 py-2 border-b">
                                            <p className="font-medium text-stone-800">{user?.name}</p>
                                            <p className="text-sm text-stone-500">{user?.email}</p>
                                        </div>
                                        <DropdownMenuItem asChild>
                                            <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                                                <FileText className="w-4 h-4" />
                                                {t('nav.reports')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                                                <User className="w-4 h-4" />
                                                {t('nav.profile')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/subscription" className="flex items-center gap-2 cursor-pointer" data-testid="nav-subscription-menu">
                                                <CreditCard className="w-4 h-4" />
                                                {t('nav.subscription')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={handleLogout} 
                                            className="text-red-600 cursor-pointer"
                                            data-testid="logout-btn"
                                        >
                                            <LogOut className="w-4 h-4 ltr:mr-2 rtl:ml-2" />
                                            {t('nav.logout')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Mobile Menu Button */}
                                <button
                                    onClick={() => setIsOpen(!isOpen)}
                                    className="md:hidden p-2 rounded-lg hover:bg-stone-100"
                                    data-testid="mobile-menu-btn"
                                >
                                    {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link to="/login">
                                    <Button variant="ghost" className="text-stone-600" data-testid="login-nav-btn">
                                        {t('nav.login')}
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6" data-testid="register-nav-btn">
                                        {t('nav.register')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isAuthenticated && isOpen && (
                    <div className="md:hidden py-4 border-t border-stone-200/50 animate-fade-in" data-testid="mobile-menu">
                        <div className="flex flex-col gap-1">
                            {navLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        onClick={() => setIsOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                                            isActive(link.href)
                                                ? 'bg-teal-500 text-white'
                                                : 'text-stone-600 hover:bg-stone-100'
                                        }`}
                                    >
                                        <Icon className="w-5 h-5" />
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
